import { INPUT_TYPES } from '../../../../shared/tabbed-table/constants';
import { IS_NULL_OR_UNDEFINED } from 'cargo-chief-commons';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';
import { UtilService } from '@service/util/util.service';

export const OFFER_TAB_TYPES = {
	ACTIVE: 'Active',
	OPEN: 'Open',
	ACCEPTED: 'Accepted',
	DECLINED: 'Declined',
};

export const OFFER_FIELDS = {
	LOAD_REFERENCE: 'Reference Number',
	CARRIER_NAME: 'Carrier Name',
	CARRIER_MC: 'MC Number',
	CARRIER_DOT: 'DOT Number',
	CARRIER_MC_DOT: 'MC / DOT',
	QUALITY_SCORE: 'Quality Score',
	LOAD_RATE: 'Offered Rate',
	BID_RATE: 'Carrier Bid',
	STATUS: 'Status',
	LAST_INTERACTION: 'Last Interaction',
	PICKUP_DATE: 'Pickup Date',
	LANE: 'Lane',
	DELIVERY_DATE: 'Delivery Date',
	ORIGIN: 'Origin',
	DESTINATION: 'Destination',
	EQUIPMENT: 'Equipment',
	CUSTOMER: 'Customer',
	USER: 'User',
	CREATED_DATE: 'Created Date',
};

export const SORT_FIELD_MAPS = {
	[OFFER_FIELDS.LOAD_REFERENCE]: 'referenceNumber',
	[OFFER_FIELDS.CARRIER_NAME]: 'businessName',
	[OFFER_FIELDS.CARRIER_MC]: 'mc',
	[OFFER_FIELDS.CARRIER_DOT]: 'dot',
	[OFFER_FIELDS.CARRIER_MC_DOT]: 'mcDot',
	[OFFER_FIELDS.QUALITY_SCORE]: 'qualityScore',
	[OFFER_FIELDS.LOAD_RATE]: 'priceToShipper',
	[OFFER_FIELDS.BID_RATE]: 'allInPrice',
	[OFFER_FIELDS.STATUS]: 'status',
	[OFFER_FIELDS.LAST_INTERACTION]: 'lastInteraction',
	[OFFER_FIELDS.PICKUP_DATE]: 'pickupDate',
	[OFFER_FIELDS.DELIVERY_DATE]: 'deliveryDate',
	[OFFER_FIELDS.ORIGIN]: 'origin',
	[OFFER_FIELDS.DESTINATION]: 'destination',
	[OFFER_FIELDS.EQUIPMENT]: 'equipment',
	[OFFER_FIELDS.CUSTOMER]: 'customer',
	[OFFER_FIELDS.USER]: 'user',
	[OFFER_FIELDS.CREATED_DATE]: 'created',
};

export let filterFieldsInputTypeMap: Map<string, string> = new Map<string, string>();

filterFieldsInputTypeMap.set(OFFER_FIELDS.LOAD_REFERENCE, INPUT_TYPES.STRING);
filterFieldsInputTypeMap.set(OFFER_FIELDS.CARRIER_NAME, INPUT_TYPES.CARRIER);
filterFieldsInputTypeMap.set(OFFER_FIELDS.CARRIER_MC, INPUT_TYPES.STRING);
filterFieldsInputTypeMap.set(OFFER_FIELDS.CARRIER_DOT, INPUT_TYPES.STRING);
filterFieldsInputTypeMap.set(OFFER_FIELDS.CARRIER_MC_DOT, INPUT_TYPES.STRING);
filterFieldsInputTypeMap.set(OFFER_FIELDS.QUALITY_SCORE, INPUT_TYPES.STRING);
filterFieldsInputTypeMap.set(OFFER_FIELDS.LOAD_RATE, INPUT_TYPES.NUMBER);
filterFieldsInputTypeMap.set(OFFER_FIELDS.BID_RATE, INPUT_TYPES.NUMBER);
filterFieldsInputTypeMap.set(OFFER_FIELDS.STATUS, INPUT_TYPES.STRING);
filterFieldsInputTypeMap.set(OFFER_FIELDS.LAST_INTERACTION, INPUT_TYPES.STRING);
filterFieldsInputTypeMap.set(OFFER_FIELDS.PICKUP_DATE, INPUT_TYPES.DATE);
filterFieldsInputTypeMap.set(OFFER_FIELDS.DELIVERY_DATE, INPUT_TYPES.DATE);
filterFieldsInputTypeMap.set(OFFER_FIELDS.ORIGIN, INPUT_TYPES.LOCATION);
filterFieldsInputTypeMap.set(OFFER_FIELDS.DESTINATION, INPUT_TYPES.LOCATION);
filterFieldsInputTypeMap.set(OFFER_FIELDS.EQUIPMENT, INPUT_TYPES.TRAILER_TYPE);
filterFieldsInputTypeMap.set(OFFER_FIELDS.CUSTOMER, INPUT_TYPES.CUSTOMER);
filterFieldsInputTypeMap.set(OFFER_FIELDS.USER, INPUT_TYPES.USERS_LIST);
filterFieldsInputTypeMap.set(OFFER_FIELDS.CREATED_DATE, INPUT_TYPES.DATE);

export function buildHeaders(
	accountSettings: AccountSettingsService,
	selectedOfferType: string,
	isAdmin: boolean
) {
	let headers = [
		{ dataName: 'select', formattedName: 'select' },
		{ dataName: 'referenceNumber', formattedName: OFFER_FIELDS.LOAD_REFERENCE },
		{ dataName: 'pickupDate', formattedName: OFFER_FIELDS.PICKUP_DATE },
		{ dataName: 'lane', formattedName: OFFER_FIELDS.LANE },
		{ dataName: 'businessName', formattedName: OFFER_FIELDS.CARRIER_NAME },
		{ dataName: 'qualityScore', formattedName: OFFER_FIELDS.QUALITY_SCORE },
		{ dataName: 'priceToShipper', formattedName: OFFER_FIELDS.LOAD_RATE },
		{ dataName: 'allInPrice', formattedName: OFFER_FIELDS.BID_RATE },
		{ dataName: 'status', formattedName: OFFER_FIELDS.STATUS },
		{ dataName: 'lastInteraction', formattedName: OFFER_FIELDS.LAST_INTERACTION },
		{ dataName: 'actions', formattedName: 'Actions' },
	];

	if (accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS || isAdmin) {
		headers.splice(4, 0, { dataName: 'customerName', formattedName: OFFER_FIELDS.CUSTOMER });
	}

	return headers;
}

export function buildHeaderDataTypes(
	utilService: UtilService,
	accountSettings: AccountSettingsService,
	selectedOfferType: string,
	isAdmin: boolean
) {
	// Build headers to get the correct order
	const headers = buildHeaders(accountSettings, selectedOfferType, isAdmin);
	// Default types for each column
	const defaultTypes = {
		referenceNumber: 'string',
		pickupDate: 'date',
		lane: 'string',
		businessName: 'string', // Use standard string type, custom type handled via customDataTypes
		customerName: 'string',
		qualityScore: 'qualityScore',
		priceToShipper: 'currency',
		allInPrice: 'currency',
		status: 'statusChip',
		lastInteraction: 'string',
		actions: 'string',
	};
	// Filter out the 'select' column and return only data column types
	return headers
		.filter((h) => h.dataName !== 'select')
		.map((h) => defaultTypes[h.dataName] || 'string');
}

export function buildDisplayedColumns(
	utilService: UtilService,
	accountSettings: AccountSettingsService,
	selectedOfferType: string,
	isAdmin: boolean
): string[] {
	let columns = [
		'select',
		'referenceNumber',
		'pickupDate',
		'lane',
		'businessName',
		'qualityScore',
		'priceToShipper',
		'allInPrice',
		'status',
		'lastInteraction',
		'actions',
	];

	if (accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS || isAdmin) {
		columns.splice(4, 0, 'customerName');
	}

	return columns;
}

export function buildSortFields() {
	return [
		OFFER_FIELDS.LOAD_REFERENCE,
		OFFER_FIELDS.CARRIER_NAME,
		OFFER_FIELDS.CARRIER_MC,
		OFFER_FIELDS.CARRIER_DOT,
		OFFER_FIELDS.CARRIER_MC_DOT,
		OFFER_FIELDS.QUALITY_SCORE,
		OFFER_FIELDS.LOAD_RATE,
		OFFER_FIELDS.BID_RATE,
		OFFER_FIELDS.STATUS,
		OFFER_FIELDS.LAST_INTERACTION,
		OFFER_FIELDS.PICKUP_DATE,
		OFFER_FIELDS.DELIVERY_DATE,
		OFFER_FIELDS.ORIGIN,
		OFFER_FIELDS.DESTINATION,
		OFFER_FIELDS.EQUIPMENT,
		OFFER_FIELDS.CUSTOMER,
		OFFER_FIELDS.USER,
		OFFER_FIELDS.CREATED_DATE,
	];
}

export function buildFilterFields(
	accountSettings: AccountSettingsService,
	isUserRoleAdminOrOwner: boolean
) {
	let fields = [
		OFFER_FIELDS.LOAD_REFERENCE,
		OFFER_FIELDS.CARRIER_NAME,
		OFFER_FIELDS.CARRIER_MC,
		OFFER_FIELDS.CARRIER_DOT,
		OFFER_FIELDS.CARRIER_MC_DOT,
		OFFER_FIELDS.QUALITY_SCORE,
		OFFER_FIELDS.LOAD_RATE,
		OFFER_FIELDS.BID_RATE,
		OFFER_FIELDS.STATUS,
		OFFER_FIELDS.LAST_INTERACTION,
		OFFER_FIELDS.PICKUP_DATE,
		OFFER_FIELDS.DELIVERY_DATE,
		OFFER_FIELDS.ORIGIN,
		OFFER_FIELDS.DESTINATION,
		OFFER_FIELDS.EQUIPMENT,
		OFFER_FIELDS.USER,
		OFFER_FIELDS.CREATED_DATE,
	];

	// Remove USER filter field if NON_ADMIN_SEE_OWN_LOADS_ONLY is enabled and user is not admin or owner
	if (accountSettings.NON_ADMIN_SEE_OWN_LOADS_ONLY && !isUserRoleAdminOrOwner) {
		fields = fields.filter((field) => field !== OFFER_FIELDS.USER);
	}

	if (accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS || isUserRoleAdminOrOwner) {
		fields.splice(2, 0, OFFER_FIELDS.CUSTOMER);
	}

	return fields;
}

export const getOffersFilters = (
	filters: any[],
	accountSettings: AccountSettingsService,
	isUserRoleAdminOrOwner: boolean,
	userId: number
): any[] => {
	if (!filters || !accountSettings || IS_NULL_OR_UNDEFINED(userId)) {
		return filters || [];
	}
	if (accountSettings.NON_ADMIN_SEE_OWN_LOADS_ONLY && !isUserRoleAdminOrOwner) {
		const hasUserFilter = filters.find((filter) => filter.filterField == OFFER_FIELDS.USER);
		if (hasUserFilter) {
			return filters.map((filter) => {
				if (filter.filterField == OFFER_FIELDS.USER) {
					return {
						...filter,
						filterText: [userId],
						filterCondition: 'and',
					};
				}
				return filter;
			});
		}
		return [
			...filters,
			{
				endValue: '',
				filterCondition: 'and',
				filterField: OFFER_FIELDS.USER,
				filterText: [userId],
				isNotDeletable: false,
				operator: '',
				startValue: '',
			},
		];
	}
	return filters;
};

export function buildActions(activeTab, customTabId, offersFacade) {
	let actionsList = [];

	actionsList.push({
		label: 'Delete Saved View',
		action: async () => {
			if (!customTabId) return;
			offersFacade.deleteCustomTab(customTabId);
			offersFacade.setActiveTab(OFFER_TAB_TYPES.ACTIVE);
		},
		disabled: (selected) => activeTab === OFFER_TAB_TYPES.ACTIVE || !customTabId,
	});

	return actionsList;
}
